import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

// Utils
import { userIsRegistered } from '../../../../../../../../common/users/userHelper';
import { getUserEntryName } from './boardEditorPermissionUtils';
import {
    isOwner,
    isFullAccess,
    isBlocked,
    canEditPermissions,
} from '../../../../../../../../common/permissions/permissionUtil';

// Actions
import { modalClosed } from '../../../../../../../components/modal/modalActions';

// Components
import LinkButton from '../../../../../../../components/buttons/LinkButton';
import PopupActionsWithConfirmationView from './PopupActionsWithConfirmationView';

// Constants
import { ModalIds } from '../../../../../../../components/modal/modalConstants';

const BoardEditorPermissionPopupActions = (props) => {
    const {
        permission,
        user,
        editable,
        removeSharedUser,
        navigateToSharedBoard,
        transferBoardOwnership,
        setUserPermissionsFull,
        userPermissions,
        closePopup,
        createRequireConfirmationFn,
    } = props;

    const name = getUserEntryName(props);

    const dispatch = useDispatch();

    const dispatchCloseShareModal = () => dispatch(modalClosed(ModalIds.SHARE));

    if (!canEditPermissions(userPermissions)) {
        return (
            <div className="permission-actions cannot-edit-permissions">
                You don&apos;t have permission to change the access of other users
            </div>
        );
    }

    if (isOwner(permission)) {
        return (
            <div className="permission-actions no-action">
                Make someone else the owner to change this person’s access
            </div>
        );
    }

    if (!editable) {
        // keeping the behaviour same in web and mobile versions
        const onNavigateToSharedBoardClick = () => {
            closePopup();
            dispatchCloseShareModal();
            navigateToSharedBoard();
        };

        return (
            <ul className="permission-actions">
                <li>
                    <LinkButton onClickFn={onNavigateToSharedBoardClick}>
                        Go to that board to change their access
                    </LinkButton>
                </li>
            </ul>
        );
    }

    const actions = [];

    if (isOwner(userPermissions) && isFullAccess(permission) && userIsRegistered(user)) {
        const onMakeOwnerClick = createRequireConfirmationFn(() => {
            closePopup();
            transferBoardOwnership();
        }, `Are you sure you want to make ${name} the owner of this board?`);

        actions.push(
            <li key="make-owner">
                <LinkButton onClickFn={onMakeOwnerClick}>Make {name} the owner of this board</LinkButton>
            </li>,
        );
    }

    if (!isFullAccess(permission)) {
        const onGiveAccessClick = () => {
            closePopup();
            setUserPermissionsFull();
        };

        actions.push(
            <li key="give-access">
                <LinkButton onClickFn={onGiveAccessClick}>
                    {isBlocked(permission) ? `Unblock ${name}` : `Give ${name} full edit access`}
                </LinkButton>
            </li>,
        );
    }

    if (!isBlocked(permission)) {
        const onRemoveClick = createRequireConfirmationFn(() => {
            closePopup();
            removeSharedUser();
        }, `Are you sure you want to remove ${name}?`);
        actions.push(
            <li key="remove-access">
                <LinkButton onClickFn={onRemoveClick}>Remove {name} from this board</LinkButton>
            </li>,
        );
    }

    return <ul className="permission-actions">{actions}</ul>;
};

BoardEditorPermissionPopupActions.propTypes = {
    editable: PropTypes.bool,
    permission: PropTypes.number,
    userPermissions: PropTypes.number,
    specificUserPermissions: PropTypes.number,
    sharedBoard: PropTypes.object,
    user: PropTypes.object,
    removeSharedUser: PropTypes.func,
    navigateToSharedBoard: PropTypes.func,
    transferBoardOwnership: PropTypes.func,
    setUserPermissionsFull: PropTypes.func,
    closePopup: PropTypes.func,

    // Provided by PopupActionsWithConfirmationView
    createRequireConfirmationFn: PropTypes.func,
};

const BoardEditorPermissionPopupActionsWithConfirmation = (props) => (
    <PopupActionsWithConfirmationView>
        <BoardEditorPermissionPopupActions {...props} />
    </PopupActionsWithConfirmationView>
);

export default BoardEditorPermissionPopupActionsWithConfirmation;
